import {useCallback, useEffect, useState} from 'react';
import {WithTranslation, withTranslation} from 'react-i18next';

import {sessionAuditApi} from 'api';
import {InformationModal, DataWrapper} from 'components';
import {Group} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {ApiError, SessionAudit} from 'types';

import {SessionAuditInfo} from '../SessionAuditInfo';

const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.SESSION_HISTORY.AUDIT.MODAL;

type Props = {
    sessionId: string;
    open: boolean;
    groups: Group[];
    onClose: () => void;
} & WithTranslation

const SessionAuditModal: React.FC<Props> = ({t, sessionId, open, groups, onClose}: Props) => {
    const [audit, setAudit] = useState<SessionAudit>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ApiError>(null);

    const fetchSessionAudit = useCallback(async () => {
        try {
            setAudit(null);
            setLoading(true);
            setError(null);
            setAudit(await sessionAuditApi.getSessionAudit(sessionId));
        } catch (e: any) {
            setError(e);
            setAudit(null);
        } finally {
            setLoading(false);
        }
    }, [sessionId]);

    const checkFetchSessionAudit = useCallback(() => {
        if (!loading && !error && audit?.sessionId !== sessionId) {
            fetchSessionAudit();
        }
    }, [audit, sessionId, loading, error, fetchSessionAudit]);

    useEffect(() => {
        if (open) {
            checkFetchSessionAudit();
        } else {
            setError(null);
        }
    }, [open, checkFetchSessionAudit]);

    return (
        <InformationModal title={`${t(T_KEYS.TITLE)}: ${sessionId}`} fullWidth maxWidth='sm' open={open} onClose={onClose}>
            {!open ?
                null :
                <DataWrapper isLoading={loading} isError={Boolean(error)}>
                    <div>
                        <div className='tw-italic tw-mb-4'>
                            {t(T_KEYS.EXPIRATION_NOTE)}
                        </div>
                        {audit && <SessionAuditInfo audit={audit} groups={groups} />}
                    </div>
                </DataWrapper>}
        </InformationModal>
    );
};

const wrappedComponent = withTranslation()(SessionAuditModal);
export {wrappedComponent as SessionAuditModal};